import { useCallback, useState } from 'react';
import {
  useMyBrandPanelQuery,
  useMyCampaignsQuery,
  useMyCatalogQuery,
} from '../../queries/brandMetrics';
import brandMetricsApi from '../../api/brandMetrics';
import { previousPeriod } from './periodCompare';
import BrandView from './BrandView';

const isoDaysAgo = (days) => {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
};

const keepPrevious = { placeholderData: (prev) => prev };

/**
 * Panel del cliente: su propia marca (resuelta en el backend por su usuario),
 * sin selector de marca ni breadcrumb. Reutiliza BrandView (header + secciones).
 */
export default function ClientPanel() {
  const [start, setStart] = useState(() => isoDaysAgo(30));
  const [end, setEnd] = useState(() => isoDaysAgo(1));
  const [compareMode, setCompareMode] = useState('previous');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');

  const priorPeriod = previousPeriod(start, end);
  const compareStart = compareMode === 'custom' ? customStart : priorPeriod.start;
  const compareEnd = compareMode === 'custom' ? customEnd : priorPeriod.end;

  const onCompareModeChange = (mode) => {
    if (mode === 'custom' && !customStart) {
      setCustomStart(priorPeriod.start);
      setCustomEnd(priorPeriod.end);
    }
    setCompareMode(mode);
  };

  const { data, isLoading, isError, error } = useMyBrandPanelQuery(start, end, keepPrevious);
  const isEcom = data?.brand?.vertical === 'ecommerce';
  const { data: compareData } = useMyBrandPanelQuery(compareStart, compareEnd, {
    enabled: Boolean(compareStart && compareEnd && isEcom),
  });

  const noPanel = isError && error?.response?.status === 404;
  const campaigns = useMyCampaignsQuery(start, end, {
    enabled: Boolean(data && !noPanel),
    ...keepPrevious,
  });

  const catalog = useMyCatalogQuery(start, end, {
    enabled: Boolean(data && !noPanel && isEcom),
    ...keepPrevious,
  });

  const previewFetcher = useCallback(
    (creativeId) => brandMetricsApi.getMyCreativePreview(creativeId),
    []
  );

  const dateControls = {
    start,
    end,
    onStartChange: (e) => setStart(e.target.value),
    onEndChange: (e) => setEnd(e.target.value),
    compareMode,
    onCompareModeChange: (e) => onCompareModeChange(e.target.value),
    customStart,
    customEnd,
    onCustomStartChange: (e) => setCustomStart(e.target.value),
    onCustomEndChange: (e) => setCustomEnd(e.target.value),
  };

  if (noPanel) {
    return (
      <div className="mx-auto max-w-7xl space-y-6 p-6">
        <h1 className="text-xl font-bold text-gray-900">Mi panel</h1>
        <p className="py-12 text-center text-sm text-gray-500">
          Tu cuenta todavía no tiene un panel asignado. Escribinos y lo activamos.
        </p>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="mx-auto max-w-7xl space-y-6 p-6">
        <h1 className="text-xl font-bold text-gray-900">Mi panel</h1>
        <p className="py-12 text-center text-sm text-red-600">
          No se pudo cargar el panel. Probá de nuevo en unos segundos.
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-6">
      <BrandView
        brand={data?.brand ?? { name: 'Mi panel' }}
        panel={{ data, isLoading, isError, previousSummary: compareData?.summary }}
        campaigns={campaigns}
        catalog={catalog}
        dateControls={dateControls}
        previewFetcher={previewFetcher}
      />
    </div>
  );
}
